import _ from 'lodash';
import { Button, Grid2 as Grid, Typography } from '@mui/material';
import { useMutation } from "@apollo/client";
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, Location, useLocation, useParams } from "react-router-dom";

import { isBuyerDetailsLocation } from '.';
import { BaseBuyerOption } from '../buyer/BaseBuyerOption';
import { Account, BuyerDetailType, BuyerOption, BuyerPerson, ExternalId, IDInput,
          PersonValue } from '../../interfaces';
import Login from '../header/Login';
import { updatePerson } from '../../queries';
import { useLambdas } from "../../utils/lambdas";
import { useGlobalStore } from "../../store";
import { CircularProgressBackdrop } from '../common';
import { sanitizeEmail } from '../../utils/email';
import CustomSnackbar from '../common/CustomSnackbar';


interface BuyerInput {
  name: string,
  website?: string,
  sellerId?: number,
  buyerDetails?: BuyerDetailValue[],
  persons?: PersonValue[],
  account?: Account
}

interface BuyerDetailValue {
  buyerOption: IDInput;
  value: string;
}

interface BasicStateItem {
  optionValue: string;
  optionId: IDInput;
  externalId?: ExternalId
}

interface BuyerCoreSectionState {
  [key: string]: BasicStateItem | undefined;
}

interface BuyerSectionState {
  [key: string]: BasicStateItem
}

interface PersonStateItem {
  optionId: IDInput;
  optionValue: PersonValue;
}

interface KeyPersonSectionState {
  [key: string]: PersonStateItem
}

interface PersonSectionState {
  [key: string]: PersonStateItem
}

export function BuyerDetails() {

  /** Set up state objects */
  const [ buyerCoreDetailValues, setBuyerCoreDetailValue ] = useState<BuyerCoreSectionState>({});
  const [ buyerDetailValues, setBuyerDetailValue ] = useState<BuyerSectionState>({});

  const [ keyPersonDetailValues, setKeyPersonDetailValue ] = useState<KeyPersonSectionState>({});
  const [ personDetailValues, setPersonDetailValue ] = useState<PersonSectionState>({});

  const [ buyerInput, setBuyerInput ] = useState<BuyerInput>();
  
  const account = useGlobalStore((s) => s.account);
  const refetchAccount = useGlobalStore((s) => s.refetch);
  const formRef = useRef<HTMLFormElement>(null);

  const [ isSubmitting, setIsSubmitting ] = useState(false);

  const [errors, setErrors] = useState(new Set());

  const [pendingSubmit, setPendingSubmit] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [snackbarData, setSnackbarData] = useState({
    message: '',
    isSuccess: true,
  });

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const hasErrors = errors.size > 0;
  
  const [ saveDefaultBuyer] = useMutation(updatePerson);

  const [, executePost] = useLambdas(
    "addBuyer",
    undefined,
    { buyer: buyerInput }
  )

  /** Read URL Parameters */
  const { updateBuyer } = useGlobalStore();
  const location: Location = useLocation();
  const { stateCode } = useParams();
  const navigate = useNavigate();

  const locationDetails = isBuyerDetailsLocation(location.state) ? location.state : undefined;
  const optionTemplate = locationDetails?.optionTemplate;
  
  useEffect(() => {
    if (!optionTemplate) {
      navigate(`/${stateCode}`)
    }
  });

  useEffect(() => {
    let buyerInputValue: BuyerInput = {
      name: buyerCoreDetailValues.CampaignName ? buyerCoreDetailValues.CampaignName.optionValue.trim() : '', 
      website: buyerCoreDetailValues.Website?.optionValue,
      sellerId: optionTemplate?.seller.id,
      account: account?.email ? { email: account.email } : undefined
    }

    buyerInputValue.persons = _.map(keyPersonDetailValues, (person) => {
      return { ...person.optionValue }
    }).concat(_.map(personDetailValues, (person) => {
      return { ...person.optionValue }
    }))

    const tempBuyerCoreDetails = _.map(buyerCoreDetailValues, (buyerOpt) => {
      if (buyerOpt) {
        return buyerOpt.externalId ? {
          value: buyerOpt.optionValue,
          buyerOption: buyerOpt.optionId,
          externalId: buyerOpt.externalId
        } : {
          value: buyerOpt.optionValue,
          buyerOption: buyerOpt.optionId
        }
      } else {
        return { value: '', buyerOption: { id: -1 }}
      }      
    });

    const tempBuyerDetails = _.map(buyerDetailValues, (buyerOpt) => {
      return buyerOpt.externalId ? {
        value: buyerOpt.optionValue,
        buyerOption: buyerOpt.optionId,
        externalId: buyerOpt.externalId
      } : {
        value: buyerOpt.optionValue,
        buyerOption: buyerOpt.optionId
      }
    });

    buyerInputValue.buyerDetails =_.concat(
      tempBuyerCoreDetails,
      tempBuyerDetails
    )
    
    setBuyerInput(buyerInputValue);
  }, [buyerCoreDetailValues, optionTemplate, account, keyPersonDetailValues, personDetailValues, buyerDetailValues])

  /** Event Handlers */
  const onBuyerCoreDetailChange = (option: string, value: string, id: number, externalId?: ExternalId) => {
    setBuyerCoreDetailValue(prevValue => ({
      ...prevValue,
      [option]: externalId ? {
        optionValue: value, optionId: { id: id }, externalId: externalId
      } : { optionValue: value, optionId: { id: id } }
    }))
  }

  const onBuyerDetailChange = (option: keyof BuyerSectionState, value: string, id: number, externalId?: ExternalId) => {
    setBuyerDetailValue(prevValue => ({
      ...prevValue,
      [option]: externalId ? {
        optionValue: value, optionId: { id: id }, externalId: externalId
      } : { optionValue: value, optionId: { id: id } }
    }))
  }

  const onKeyPersonDetailChange = (option: keyof KeyPersonSectionState, value: PersonValue, id: number) => {
    setKeyPersonDetailValue(prevValue => ({
      ...prevValue,
      [option]: { optionValue: value, optionId: { id: id }}
    }))
  }

  const onPersonDetailChange = (option: keyof PersonSectionState, value: PersonValue, id: number) => {
    setPersonDetailValue(prevValue => ({
      ...prevValue,
      [option]: { optionValue: value, optionId: { id: id }}
    }))
  }

  const onErrorChange = useCallback((id: number, hasError: boolean) => {
    setErrors((prevErrors) => {
      const newErrors = new Set(prevErrors);

      if (hasError) {
        newErrors.add(id);
      } else {
        newErrors.delete(id);
      }
      return newErrors;
    });
  }, []);

  const onSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    let newBuyer;
    try {
      const buyerResult = await executePost();
      newBuyer = buyerResult.data;
    } catch (error) {
      // The committee was not created - reloading is safe here
      setSnackbarData({
        message: 'Error creating committee',
        isSuccess: false,
      });
      setSnackbarOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      setIsSubmitting(false);
      return;
    }

    try {
      const buyerPerson = newBuyer.buyerPersons.find((bp: BuyerPerson) =>
        sanitizeEmail(bp.person.email) === sanitizeEmail(account?.email)
      );

      if (buyerPerson) {
        await saveDefaultBuyer({
          variables: {
            person: {
              id: buyerPerson.person.id,
              defaultBuyerId: newBuyer.id
            }
          }
        });
      } else {
        console.error(
          `No buyerPerson on new buyer ${newBuyer.id} matched account email; skipping default buyer update`
        );
      }
    } catch (error) {
      console.error('Failed to save default buyer for new committee', error);
    }

    updateBuyer(newBuyer);

    try {
      await refetchAccount();
    } catch (error) {
      console.error('Failed to refetch account after creating committee', error);
    }

    navigate('/', { state: { newBuyerCreated: true } });
    setIsSubmitting(false);
  };

  // Do a more thorough check - auth0 probably a lot slower than 100ms sometimes
  // Wait for submit, account email, form & form has no errors
  useEffect(() => {
    if (pendingSubmit && account?.email && formRef.current && !hasErrors) {
      formRef.current.requestSubmit();
      setPendingSubmit(false);
    }
  }, [pendingSubmit, account, hasErrors]);

  const buyerOptions = _.sortBy(optionTemplate?.buyerOptions, ['uiOrder']);

  const buyerCoreDetails = _.filter(buyerOptions, { detailType: BuyerDetailType.buyerCore});
  const buyerDetails = _.filter(buyerOptions, { detailType: BuyerDetailType.buyer});
  const keyPersonDetails = _.filter(buyerOptions, { detailType: BuyerDetailType.keyPerson});
  const personDetails = _.filter(buyerOptions, { detailType: BuyerDetailType.otherPerson});

  // Allows for marking subsequent people as the same as the first
  const firstPersonKey = keyPersonDetails.length > 0 ? keyPersonDetails[0].key : undefined;
  const firstPersonLabel = keyPersonDetails.length > 0 ? keyPersonDetails[0].label : undefined;
  const firstPersonValue = keyPersonDetails.length > 0 ? Object.values(keyPersonDetailValues)[0]?.optionValue : undefined;

  return (
    <Grid container component="form" ref={formRef} onSubmit={onSubmit} sx={{ mb: 4 }}>
      <Grid container sx={{ m: 2 }} spacing={2}>
        <Grid size={12}>
          <Typography variant={'h5'}>
            Organization Details
          </Typography>
          <Typography variant={'subtitle2'} sx={{ paddingBottom: 2 }}>
            Tell us a little more about { locationDetails?.showStaffMessage ? "who's running" : "you" }
          </Typography>
        </Grid>
        <Grid container sx={{ m: 2 }}>
          {
            buyerCoreDetails?.map((option: BuyerOption) => {
              return <BaseBuyerOption key={option.id} option={option} onDetailChange={onBuyerCoreDetailChange} onErrorChange={onErrorChange} />
            })
          }
        </Grid>
        <Grid container sx={{ m: 2 }}>
          {
            buyerDetails?.map((option: BuyerOption) => {
              return <BaseBuyerOption key={option.id} option={option} onDetailChange={onBuyerDetailChange} onErrorChange={onErrorChange} />
            })
          }
        </Grid>
        <Grid size={12} sx={{ mb: 2 }}>
          Please provide information about yourself and any others on the campaign:
        </Grid>
        <Grid container sx={{ m: 2 }} width="100%">
          {
            keyPersonDetails?.map((option: BuyerOption) => {
              return <BaseBuyerOption
                      key={option.id} option={option} onDetailChange={onKeyPersonDetailChange} onErrorChange={onErrorChange}
                      firstPersonKey={firstPersonKey} firstPersonLabel={firstPersonLabel} firstPersonValue={firstPersonValue}/>
            })
          }
          {
            personDetails?.map((option: BuyerOption) => {
              return <BaseBuyerOption
                      key={option.id} option={option} onDetailChange={onPersonDetailChange} onErrorChange={onErrorChange}
                      firstPersonKey={firstPersonKey} firstPersonLabel={firstPersonLabel} firstPersonValue={firstPersonValue}/>
            })
          }
        </Grid>
      </Grid>
      <Grid size={12} textAlign="center">
        <CircularProgressBackdrop open={isSubmitting} />
        {_.isEmpty(account) ? (
          <Login onLoginSuccess={() => setPendingSubmit(true)} />
        ) : (
          <Button
            variant="contained"
            className="btn-margin"
            size="large"
            disableElevation
            color="primary"
            type="submit"
            disabled={hasErrors}
          >
            <Typography variant={'subtitle2'}>
              Submit
            </Typography>
          </Button>
        )}
      </Grid>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarData.message}
        onClose={handleCloseSnackbar}
        isSuccess={snackbarData.isSuccess}
      />
    </Grid>
  );
}
